import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import type { CountryProps } from '../../pages/Cannabis';

function getNext420(): { date: Date; label: string } {
  const now = new Date();
  // Next 4:20 PM today?
  const today420pm = new Date(now);
  today420pm.setHours(16, 20, 0, 0);
  if (now < today420pm) return { date: today420pm, label: '4:20 PM Today' };

  // Next 4:20 AM tomorrow?
  const tmrw420am = new Date(now);
  tmrw420am.setDate(tmrw420am.getDate() + 1);
  tmrw420am.setHours(4, 20, 0, 0);
  return { date: tmrw420am, label: '4:20 AM Tomorrow' };
}

function getNextApril20(): { date: Date; daysAway: number } {
  const now = new Date();
  let year = now.getFullYear();
  let april20 = new Date(year, 3, 20); // Month is 0-indexed
  if (now > april20) {
    year++;
    april20 = new Date(year, 3, 20);
  }
  const diff = april20.getTime() - now.getTime();
  return { date: april20, daysAway: Math.ceil(diff / (1000 * 60 * 60 * 24)) };
}

function formatCountdown(ms: number): { hours: string; minutes: string; seconds: string } {
  if (ms <= 0) return { hours: '00', minutes: '00', seconds: '00' };
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    hours: h.toString().padStart(2, '0'),
    minutes: m.toString().padStart(2, '0'),
    seconds: s.toString().padStart(2, '0'),
  };
}

const FUN_FACTS = [
  '420 originated from a group of San Rafael, CA high schoolers called "the Waldos" in 1971. They met at 4:20 PM to search for an abandoned cannabis crop.',
  'The California Senate Bill that decriminalized marijuana was numbered SB 420.',
  'There are 420 active chemical compounds in cannabis, though this is coincidental.',
  'Colorado sold $44 million worth of cannabis on April 20, 2023 alone.',
  'In the Netherlands, 4/20 isn\'t celebrated because cannabis is available year-round in coffeeshops.',
  'Bob Marley died on May 11, 1981 — not on 4/20 as many believe.',
  'The Grateful Dead\'s connection to 420 culture helped spread it from California to the world.',
  'April 20 is also the birthday of the cannabis strain "420 Kush."',
  'Denver\'s annual 4/20 rally at Civic Center Park draws over 50,000 people.',
  'Canada legalized recreational cannabis on October 17, 2018 — they chose to NOT make it 4/20.',
];

export default function CountdownTab(_props: CountryProps) {
  const [timeLeft, setTimeLeft] = useState(0);
  const [next420, setNext420] = useState(getNext420());
  const [factIndex, setFactIndex] = useState(0);
  const april20 = getNextApril20();

  useEffect(() => {
    const timer = setInterval(() => {
      const n = getNext420();
      setNext420(n);
      setTimeLeft(n.date.getTime() - Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const id = setInterval(() => setFactIndex(i => (i + 1) % FUN_FACTS.length), 8000);
    return () => clearInterval(id);
  }, []);

  const cd = formatCountdown(timeLeft);
  const is420Now = timeLeft <= 0 && timeLeft > -60000;

  return (
    <div className="space-y-8 max-w-xl mx-auto">
      {/* Main countdown */}
      <div className="text-center space-y-6 py-8">
        <p className="text-white/40 text-xs uppercase tracking-widest">Next 4:20</p>
        <p className="text-white/60 text-sm">{next420.label}</p>

        {is420Now ? (
          <motion.div animate={{ scale: [1, 1.1, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
            <p className="text-6xl font-black text-[#4ade80]">IT'S 4:20!</p>
            <p className="text-[#4ade80]/60 text-lg mt-2">Light up!</p>
          </motion.div>
        ) : (
          <div className="flex justify-center gap-4">
            {[
              { val: cd.hours, label: 'HRS' },
              { val: cd.minutes, label: 'MIN' },
              { val: cd.seconds, label: 'SEC' },
            ].map(unit => (
              <div key={unit.label} className="text-center">
                <div className="w-20 h-20 rounded-2xl border border-white/10 bg-white/[0.02] flex items-center justify-center">
                  <span className="text-3xl font-bold text-white font-mono">{unit.val}</span>
                </div>
                <p className="text-white/30 text-[10px] mt-2 tracking-widest">{unit.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* April 20 countdown */}
      <div className="rounded-2xl border border-[#4ade80]/20 bg-[#4ade80]/5 p-5 text-center space-y-2">
        <p className="text-white/40 text-xs uppercase tracking-wider">Next April 20th</p>
        <p className="text-[#4ade80] font-bold text-3xl">{april20.daysAway} days</p>
        <p className="text-white/40 text-xs">{april20.date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      {/* Fun fact carousel */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-3 min-h-[120px]">
        <p className="text-[#d4a574] text-xs font-semibold uppercase tracking-wider">420 Fun Fact</p>
        <motion.p
          key={factIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="text-white/60 text-sm leading-relaxed"
        >
          {FUN_FACTS[factIndex]}
        </motion.p>
        <div className="flex justify-center gap-1">
          {FUN_FACTS.map((_, i) => (
            <button key={i} onClick={() => setFactIndex(i)}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${i === factIndex ? 'bg-[#d4a574]' : 'bg-white/10'}`} />
          ))}
        </div>
      </div>

      {/* History of 420 */}
      <div className="rounded-xl border border-white/5 bg-white/[0.01] p-4 space-y-3">
        <h4 className="text-white/60 text-xs font-semibold">The Origin of 420</h4>
        <p className="text-white/40 text-xs leading-relaxed">
          In 1971, five San Rafael High School students — Steve Capper, Dave Reddix, Jeffrey Noel, Larry Schwartz, and Mark Gravitch — used "420" as a code word. They would meet at 4:20 PM by the Louis Pasteur statue on campus to search for an abandoned cannabis plot based on a hand-drawn treasure map. They never found the crop, but "420" stuck as their code for smoking. Through connections to the Grateful Dead, the term spread through Deadhead culture and eventually worldwide. High Times magazine popularized it in the 1990s.
        </p>
      </div>
    </div>
  );
}
